/**
 * TokenRefreshService - Proactive token refresh to prevent session expiration
 *
 * Decodes the JWT access token to determine expiration time and automatically
 * refreshes the token before it expires.
 */

import axios from 'axios';
import env from '../../config/env';
import LocalStorageService from './LocalStorageService';

class TokenRefreshService {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly REFRESH_MARGIN_MS = 2 * 60 * 1000; // Refresh 2 minutes before expiry
  private readonly MIN_REFRESH_INTERVAL_MS = 30 * 1000; // Minimum 30 seconds between refreshes
  private lastRefreshAttempt: number = 0;
  private isRefreshing: boolean = false;

  /**
   * Decode a JWT token to extract the payload (without verification)
   */
  private decodeToken(token: string): { exp?: number; iat?: number } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Get the expiration time of the current access token in milliseconds
   */
  private getTokenExpirationMs(): number | null {
    const token = LocalStorageService.getAccessToken();
    if (!token) return null;

    const decoded = this.decodeToken(token);
    if (!decoded?.exp) return null;

    return decoded.exp * 1000; // Convert seconds to milliseconds
  }

  /**
   * Calculate when we should refresh the token
   */
  private getNextRefreshTime(): number | null {
    const expirationMs = this.getTokenExpirationMs();
    if (!expirationMs) return null;

    const now = Date.now();
    const refreshTime = expirationMs - this.REFRESH_MARGIN_MS;

    // If token is already expired or about to expire very soon, refresh immediately
    if (refreshTime <= now) {
      return now + 100; // Small delay to avoid tight loops
    }

    return refreshTime;
  }

  /**
   * Perform the actual token refresh
   */
  private async doRefresh(): Promise<boolean> {
    // Prevent concurrent refresh attempts
    if (this.isRefreshing) {
      console.debug('[TokenRefresh] Already refreshing, skipping');
      return false;
    }

    // Rate limit refresh attempts
    const now = Date.now();
    if (now - this.lastRefreshAttempt < this.MIN_REFRESH_INTERVAL_MS) {
      console.debug('[TokenRefresh] Rate limited, skipping');
      return false;
    }

    this.isRefreshing = true;
    this.lastRefreshAttempt = now;

    try {
      const refreshToken = LocalStorageService.getRefreshToken();
      if (!refreshToken) {
        console.warn('[TokenRefresh] No refresh token available');
        return false;
      }

      console.debug('[TokenRefresh] Proactively refreshing token...');

      const response = await axios.post(`${env.API_URL}/api/admin/token/refresh/`, {
        refresh: refreshToken
      });

      const newAccessToken = response.data.access;
      const newRefreshToken = response.data.refresh;
      if (newAccessToken) {
        LocalStorageService.setAccessToken(newAccessToken);
        if (newRefreshToken) {
          LocalStorageService.setRefreshToken(newRefreshToken);
        }
        console.debug('[TokenRefresh] Token refreshed successfully');

        // Schedule next refresh
        this.scheduleRefresh();
        return true;
      }

      return false;
    } catch (error: any) {
      console.error('[TokenRefresh] Failed to refresh token:', error?.message || error);

      // If refresh token is invalid/expired, clear auth and redirect to login
      if (error?.response?.status === 401) {
        console.warn('[TokenRefresh] Refresh token expired, redirecting to login');
        LocalStorageService.clearAuthData();
        window.location.href = '/login';
      }

      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Schedule the next token refresh based on token expiration
   */
  private scheduleRefresh(): void {
    // Clear any existing timer
    this.stop();

    const nextRefreshTime = this.getNextRefreshTime();
    if (!nextRefreshTime) {
      console.debug('[TokenRefresh] No token or unable to determine expiration');
      return;
    }

    const delay = Math.max(nextRefreshTime - Date.now(), 1000);
    const expirationMs = this.getTokenExpirationMs();
    const expiresIn = expirationMs ? Math.round((expirationMs - Date.now()) / 1000) : '?';

    console.debug(`[TokenRefresh] Token expires in ${expiresIn}s, scheduling refresh in ${Math.round(delay / 1000)}s`);

    this.refreshTimer = setTimeout(async () => {
      await this.doRefresh();
    }, delay);
  }

  /**
   * Start the proactive token refresh service
   */
  start(): void {
    const token = LocalStorageService.getAccessToken();
    if (!token) {
      console.debug('[TokenRefresh] No access token, not starting');
      return;
    }

    console.debug('[TokenRefresh] Starting proactive token refresh service');
    this.scheduleRefresh();
  }

  /**
   * Stop the proactive token refresh service
   */
  stop(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Force an immediate token refresh
   */
  async forceRefresh(): Promise<boolean> {
    return this.doRefresh();
  }

  /**
   * Check if the current token is expired or about to expire
   */
  isTokenExpiringSoon(): boolean {
    const expirationMs = this.getTokenExpirationMs();
    if (!expirationMs) return true;

    return Date.now() >= expirationMs - this.REFRESH_MARGIN_MS;
  }

  /**
   * Get time until token expires in seconds (for debugging)
   */
  getTimeUntilExpiry(): number | null {
    const expirationMs = this.getTokenExpirationMs();
    if (!expirationMs) return null;

    return Math.max(0, Math.round((expirationMs - Date.now()) / 1000));
  }
}

// Export singleton instance
const tokenRefreshService = new TokenRefreshService();
export default tokenRefreshService;
